import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 30000;

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

/** Admin header bell: new service requests from clients and website quote/recovery requests. */
export default function AdminNotificationsBell() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const seenIds = useRef(null); // ids already known; null until the first load (no toasts for old items)

  const describe = useCallback((n) => {
    const d = n.data || {};
    if (n.type === 'new_service') {
      return {
        title: t('admin_notif_new_service_title', { client: d.client_name || '—' }),
        body: [d.car_plate, [d.pickup_address, d.delivery_address].filter(Boolean).join(' → '), d.pickup_date]
          .filter(Boolean).join(' · '),
      };
    }
    if (n.type === 'website_quote') {
      return {
        title: t('admin_notif_website_quote_title', { name: d.name || '—' }),
        body: [[d.pickup_address, d.delivery_address].filter(Boolean).join(' → '), d.pickup_date, d.phone, d.email]
          .filter(Boolean).join(' · '),
      };
    }
    if (n.type === 'website_recovery') {
      return {
        title: t('admin_notif_website_recovery_title', { company: d.company || d.name || '—' }),
        body: [d.name, d.location, d.urgency, d.phone, d.email].filter(Boolean).join(' · '),
      };
    }
    return { title: n.type, body: '' };
  }, [t]);

  const markRead = useCallback(async (id) => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/notifications/${id}/read`, { method: 'POST', headers: authHeaders() });
    } catch (e) { /* best effort */ }
    setItems((prev) => prev.map((n) => (n.id === id && !n.read ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
  }, []);

  const openNotification = useCallback((n) => {
    if (!n.read) markRead(n.id);
    setOpen(false);
    if (n.order_id) navigate(`/admin/servicio/${n.order_id}`);
  }, [markRead, navigate]);

  const load = useCallback(async () => {
    if (!localStorage.getItem('admin_token')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/notifications?limit=30`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      const list = data.notifications || [];
      if (seenIds.current) {
        list
          .filter((n) => !n.read && !seenIds.current.has(n.id))
          .slice(0, 3)
          .forEach((n) => {
            const { title, body } = describe(n);
            toast(title, {
              description: body,
              duration: 10000,
              action: n.order_id ? { label: t('admin_notif_open'), onClick: () => openNotification(n) } : undefined,
            });
          });
      }
      seenIds.current = new Set(list.map((n) => n.id));
      setItems(list);
      setUnread(data.unread_count || 0);
    } catch (e) {
      // network hiccup: try again on the next poll
    }
  }, [describe, openNotification, t]);

  useEffect(() => {
    load();
    const timer = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => { clearInterval(timer); window.removeEventListener('focus', onFocus); };
  }, [load]);

  const markAllRead = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/admin/notifications/read-all`, { method: 'POST', headers: authHeaders() });
    } catch (e) { /* best effort */ }
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(i18n.language?.startsWith('es') ? 'es-ES' : 'en-GB', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch (e) { return ''; }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative p-2" aria-label={t('admin_notif_title')} data-testid="admin-notifications-bell">
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="admin-notifications-count">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="admin-notifications-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b">
          <span className="font-semibold text-sm">{t('admin_notif_title')}</span>
          {unread > 0 && (
            <button type="button" onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
              {t('admin_notif_mark_all_read')}
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6">{t('admin_notif_empty')}</p>
          ) : (
            items.map((n) => {
              const { title, body } = describe(n);
              return (
                <button
                  type="button"
                  key={n.id}
                  onClick={() => openNotification(n)}
                  className={`w-full text-left px-3 py-2 border-b last:border-b-0 hover:bg-gray-50 ${n.read ? '' : 'bg-blue-50/60'}`}
                  data-testid="admin-notification-item"
                >
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-600 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className={`text-sm ${n.read ? 'text-gray-700' : 'font-semibold text-gray-900'}`}>{title}</p>
                      {body && <p className="text-xs text-gray-500 truncate">{body}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">{fmt(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
